import React, { useState, useEffect, useContext } from "react";
import { X } from "lucide-react";
import { UserContext } from "../../UserContext";

const PastHistory = ({ onClose }) => {
  const [groupedSessions, setGroupedSessions] = useState({});
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [chatDate, setChatDate] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [canContinue, setCanContinue] = useState(false);

  const { user } = useContext(UserContext);
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

  /* ------------------------- Group sessions by month ------------------------- */
  const groupSessionsByMonth = (sessions) => {
    return sessions.reduce((acc, session) => {
      const date = new Date(session.created_at);
      const monthYear = date.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
      if (!acc[monthYear]) acc[monthYear] = [];
      acc[monthYear].push(session);
      return acc;
    }, {});
  };

  /* ------------------------- Fetch all sessions ------------------------- */
  useEffect(() => {
    const fetchSessions = async () => {
      if (!user?.user_id) return;
      try {
        const response = await fetch(
          `${apiBaseURL}/users/${user.user_id}/allsessions`
        );
        if (!response.ok) throw new Error("Failed to fetch sessions");
        const data = await response.json();
        const sorted = data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setGroupedSessions(groupSessionsByMonth(sorted));
      } catch (err) {
        console.error("Error fetching sessions:", err);
      }
    };
    fetchSessions();
  }, [user, apiBaseURL]);

  /* ------------------------- Fetch chat for selected session ------------------------- */
  useEffect(() => {
    if (!selectedSessionId) return;
    const fetchChat = async () => {
      setIsChatLoading(true);
      try {
        const res = await fetch(
          `${apiBaseURL}/sessionSteps/${selectedSessionId}`
        );
        if (res.ok) {
          const data = await res.json();
          setChatData(data);
          if (data.length > 0) {
            const date = new Date(data[0].created_at).toLocaleDateString(
              "en-GB",
              { day: "numeric", month: "long", year: "numeric" }
            );
            setChatDate(date);
          }
          const lastMessage = data[data.length - 1];
          setCanContinue(
            lastMessage?.coach_response?.trim()?.toLowerCase() ===
              "end of the session"
          );
        }
      } catch (err) {
        console.error("Error fetching chat:", err);
      } finally {
        setIsChatLoading(false);
      }
    };
    fetchChat();
  }, [selectedSessionId, apiBaseURL]);

  /* ------------------------- Continue Previous Conversation ------------------------- */
  const continuePreviousConversation = async () => {
    try {
      await fetch(`${apiBaseURL}/users/${user.user_id}/session/end`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      await fetch(`${apiBaseURL}/users/Previous_session/${selectedSessionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
      });
      console.log("Continue previous conversation");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("open-reflection-chat"));
      }
      onClose?.();
    } catch (err) {
      console.error("Error continuing session:", err);
    }
  };

  /* ------------------------- Render ------------------------- */
  return (
    <div className="w-[380px] max-h-[600px] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden font-gotham">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b">
        <h2 className="text-lg font-semibold text-[#323232]">Chat History</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {!selectedSessionId ? (
          Object.keys(groupedSessions).length > 0 ? (
            Object.entries(groupedSessions).map(([month, sessions]) => (
              <div key={month} className="mb-4">
                <h3 className="text-sm font-semibold text-gray-600 mb-2">
                  {month}
                </h3>
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => setSelectedSessionId(session.session_id)}
                      className="border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 cursor-pointer transition"
                    >
                      <p className="text-xs text-gray-400">
                        {new Date(session.created_at).toLocaleDateString(
                          "en-GB",
                          { day: "numeric", month: "long", year: "numeric" }
                        )}
                      </p>
                      <p className="text-sm text-gray-800 truncate">
                        {session.coachee_response}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center h-60">
              <div className="w-6 h-6 border-4 border-gray-300 border-t-[#54B0AF] rounded-full animate-spin"></div>
            </div>
          )
        ) : (
          <div>
            <button
              onClick={() => setSelectedSessionId(null)}
              className="text-[#54B0AF] text-sm font-semibold mb-3"
            >
              ← Back
            </button>
            <h4 className="text-sm text-gray-500 mb-2">{chatDate}</h4>

            {!isChatLoading ? (
              <div className="space-y-3 overflow-y-auto max-h-[400px]">
                {chatData.map((m, i) => (
                  <div key={i}>
                    <p className="text-gray-600 text-sm mb-1">
                      {m.coachee_response}
                    </p>
                    <p className="font-medium text-[#323232] text-sm">
                      {m.coach_response}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex justify-center items-center h-60">
                <div className="w-6 h-6 border-4 border-gray-300 border-t-[#54B0AF] rounded-full animate-spin"></div>
              </div>
            )}

            {!canContinue && (
              <div className="mt-4 flex justify-center">
                <button
                  className="bg-[#5CA7A0] text-white text-sm font-medium font-gotham py-2 px-6 rounded-full hover:opacity-90 transition"
                  onClick={continuePreviousConversation}
                >
                  Continue Chat
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastHistory;
